import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

export default function ContactSection() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    userEmail: '',
    location: '',
    emergencyType: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d67bjq6a728k8ctu1q70', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          phone: '',
          userEmail: '',
          location: '',
          emergencyType: '',
          message: '',
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (err) {
      console.error('Form submission error:', err);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <section id="iletisim" className="py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-sm font-semibold text-red-600 tracking-wider uppercase">
            İletişim
          </span>
          <h4 className="text-3xl lg:text-4xl font-bold text-gray-900 mt-3 mb-4">
            <a href="#iletisim" className="cursor-default">
              Bize Ulaşın
            </a>
          </h4>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Acil durumlarınız veya teklif talepleriniz için hemen iletişime geçin
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-5 gap-12">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-3"
          >
            <form
              id="emergency-contact-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="bg-white rounded-2xl p-8 shadow-sm"
            >
              <div className="grid md:grid-cols-2 gap-5 mb-5">
                <div>
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium text-gray-700 mb-1.5"
                  >
                    Ad Soyad *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                    placeholder="Adınız ve soyadınız"
                  />
                </div>
                <div>
                  <label
                    htmlFor="phone"
                    className="block text-sm font-medium text-gray-700 mb-1.5"
                  >
                    Telefon *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                    placeholder="0555 123 45 67"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-5 mb-5">
                <div>
                  <label
                    htmlFor="userEmail"
                    className="block text-sm font-medium text-gray-700 mb-1.5"
                  >
                    E-posta *
                  </label>
                  <input
                    type="email"
                    id="userEmail"
                    name="userEmail"
                    value={formData.userEmail}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                    placeholder="ornek@email.com"
                  />
                </div>
                <div>
                  <label
                    htmlFor="emergencyType"
                    className="block text-sm font-medium text-gray-700 mb-1.5"
                  >
                    Konu *
                  </label>
                  <select
                    id="emergencyType"
                    name="emergencyType"
                    value={formData.emergencyType}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all bg-white"
                  >
                    <option value="">Seçiniz</option>
                    <option value="kesif">Keşif Talebi</option>
                    <option value="bakim">Bakım Talebi</option>
                    <option value="kurulum">Kurulum Talebi</option>
                    <option value="projelendirme">Projelendirme Talebi</option>
                  </select>
                </div>
              </div>

              <div className="mb-5">
                <label
                  htmlFor="location"
                  className="block text-sm font-medium text-gray-700 mb-1.5"
                >
                  Konum/Adres
                </label>
                <input
                  type="text"
                  id="location"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                  placeholder="Adresiniz"
                />
              </div>

              <div className="mb-6">
                <label
                  htmlFor="message"
                  className="block text-sm font-medium text-gray-700 mb-1.5"
                >
                  Mesajınız
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all resize-none"
                  placeholder="Mesajınızı yazın (maksimum 500 karakter)"
                />
                <p className="text-xs text-gray-400 mt-1">
                  {formData.message.length}/500
                </p>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-red-600 text-white py-3.5 rounded-lg font-semibold hover:bg-red-700 transition-all whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i> Gönderiliyor...
                  </span>
                ) : (
                  'Mesaj Gönder'
                )}
              </button>

              {submitStatus === 'success' && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-4 bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-sm"
                >
                  <i className="ri-check-line mr-2"></i>
                  Mesajınız başarıyla gönderildi. En kısa sürede dönüş yapacağız.
                </motion.div>
              )}
              {submitStatus === 'error' && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-4 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm"
                >
                  <i className="ri-error-warning-line mr-2"></i>
                  Bir hata oluştu. Lütfen tekrar deneyin.
                </motion.div>
              )}
            </form>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-2 space-y-6"
          >
            <div className="bg-gradient-to-br from-red-600 to-red-700 rounded-2xl p-7 text-white">
              <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-xl mb-4">
                <i className="ri-phone-line text-xl"></i>
              </div>
              <h3 className="text-lg font-bold mb-1">Acil Destek Hattı</h3>
              <a href="tel:+903126660636" className="text-2xl font-bold hover:underline cursor-pointer">
                0312 666 06 36
              </a>
              <p className="text-red-100 text-sm mt-2">7/24 Hizmetinizdeyiz</p>
            </div>

            <div className="bg-white rounded-2xl p-7 shadow-sm">
              <div className="w-12 h-12 flex items-center justify-center bg-red-50 rounded-xl mb-4">
                <i className="ri-mail-line text-xl text-red-600"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-1">E-posta</h3>
              <a
                href="mailto:info@yanginalgila.com"
                className="text-red-600 font-medium text-sm hover:underline cursor-pointer"
              >
                info@yanginalgila.com
              </a>
              <p className="text-gray-500 text-sm mt-2">İş günleri 1 saat içinde yanıt</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
